"use client";

import * as React from "react";
import { Check } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const tiers = [{
  id: "Adults",
  name: "Adults",
  description: "Personalized primary care tailored to your unique health journey ages 19 to 64.",
  isPopular: false
}, {
  id: "family",
  name: "Family",
  description: "Comprehensive coverage for your household with dedicated support.",
  isPopular: true
}, {
  id: "couple",
  name: "Couple",
  description: "Shared wellness for partners seeking accessible, high-quality care.",
  isPopular: false
}, {
  id: "senior",
  name: "Senior",
  description: "Personalized attention and dedicated chronic disease management for seniors age 65+.",
  isPopular: false
}, {
  id: "kids",
  name: "Kids",
  description: "Pediatric focus for children ages 4 to 18.",
  isPopular: false
}];
const prices = [{
  tierId: "Adults",
  type: "studio",
  price: "$90"
}, {
  tierId: "Adults",
  type: "location",
  price: "$990"
}, {
  tierId: "family",
  type: "studio",
  price: "$250"
}, {
  tierId: "family",
  type: "location",
  price: "$2,750"
}, {
  tierId: "couple",
  type: "studio",
  price: "$170"
}, {
  tierId: "couple",
  type: "location",
  price: "$1,870"
}, {
  tierId: "senior",
  type: "studio",
  price: "$110"
}, {
  tierId: "senior",
  type: "location",
  price: "$1,210"
}, {
  tierId: "kids",
  type: "studio",
  price: "$55"
}, {
  tierId: "kids",
  type: "location",
  price: "$605"
}];
const features = [{
  tierId: "Adults",
  text: "Unlimited Office Visits for One Adult"
}, {
  tierId: "family",
  text: "4 or More Family Members"
}, {
  tierId: "Senior",
  text: "Unlimited Office Visits for One Senior"
}, {
  tierId: "couple",
  text: "Two Adult Members"
}, {
  tierId: "Kids",
  text: "Unlimited Office Visits for One Child"
}];
const tabOptions = [{
  id: "studio",
  label: "Monthly"
}, {
  id: "location",
  label: "Annual"
}];
export default function InvestmentAndPackages() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="75308" id="membership-plans" className="bg-background text-foreground py-24 sm:py-32 overflow-hidden" ref={ref}>
      <div className={`container mx-auto px-6 lg:px-8 max-w-7xl transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="mx-auto max-w-2xl text-center mb-16">
          <h2 className="text-base/7 font-semibold text-primary uppercase tracking-widest mb-2 font-mono">
            Membership
          </h2>
          <p className="mt-2 text-4xl font-serif tracking-tight sm:text-5xl text-foreground">
            Membership Plans
          </p>
          <p className="mt-6 text-lg leading-8 text-muted-foreground font-sans max-w-xl mx-auto">
            Experience healthcare without the hurdles. Our direct primary care model ensures transparent pricing and unlimited access to your medical team.
          </p>
        </div>

        <Tabs defaultValue="studio" className="w-full flex flex-col items-center">
          <TabsList className="mb-12 grid w-full max-w-md grid-cols-2 bg-muted/50 rounded-full p-1">
            {tabOptions.map((tab, i) => <TabsTrigger key={tab.id} value={tab.id} data-index={i} className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground transition-all duration-400">
                {tab.label}
              </TabsTrigger>)}
          </TabsList>

          {tabOptions.map((tab, tabIndex) => <TabsContent key={tab.id} value={tab.id} className="w-full mt-0" data-index={tabIndex}>
              <div className="isolate mx-auto grid max-w-md grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-3 items-center">
                {tiers.map((tier, tierIndex) => {
              const tierPrice = prices.find(p => p.tierId === tier.id && p.type === tab.id)?.price;
              const tierFeatures = features.filter(f => f.tierId === tier.id);
              return <Card key={tier.id} data-index={tierIndex} className="text-card-foreground gap-6 py-6 flex flex-col justify-between transition-all duration-500 rounded-2xl border-border/50 shadow-sm z-0 bg-card/50 !h-[400px] lg:scale-100">
                      <CardHeader className="pb-8 pt-10 px-8">
                        <div className="flex items-center justify-between gap-x-4">
                          <CardTitle className="text-2xl font-serif text-foreground">
                            {tier.name}
                          </CardTitle>
                          {tier.isPopular && <Badge variant="secondary" className="bg-accent text-accent-foreground hover:bg-accent/90 border-none px-3 py-1 text-xs font-semibold tracking-wide uppercase font-mono">
                              Most Popular
                            </Badge>}
                        </div>
                        <CardDescription className="mt-4 text-sm leading-6 text-muted-foreground font-sans h-12">
                          {tier.description}
                        </CardDescription>
                        <div className="mt-6 flex items-baseline gap-x-1">
                          <span className="text-5xl font-serif tracking-tight text-foreground">
                            {tierPrice}
                          </span>
                          <span className="text-sm font-sans text-muted-foreground">
                            /{tab.id === "studio" ? "mo" : "yr"}
                          </span>
                        </div>
                      </CardHeader>
                      <CardContent className="px-8 pb-8 flex-1">
                        <ul role="list" className="mt-2 space-y-4 text-sm leading-6 text-muted-foreground font-sans">
                          {tierFeatures.map((feature, featureIndex) => <li key={featureIndex} data-index={featureIndex} className="flex gap-x-3 items-start">
                              <Check className="h-5 w-5 flex-none text-ring mt-0.5" aria-hidden="true" />
                              <span>{feature.text}</span>
                            
                            </li>)}
                        </ul>
                      </CardContent>
                      <CardFooter className="px-8 pb-10 pt-0">
                        <Button asChild variant={tier.isPopular ? "default" : "outline"} className={`w-full font-sans tracking-wide rounded-full transition-all duration-400 ${tier.isPopular ? "bg-primary text-primary-foreground hover:bg-primary/90" : "hover:bg-accent hover:text-accent-foreground"}`} size="lg">
                          <Link to="/RegisterPage">Select Plan</Link>
                        </Button>
                      </CardFooter>
                    </Card>;
            })}
              </div>
            </TabsContent>)}
        </Tabs>
      </div>
    </section>;
}